import { AbstractControl, ValidationErrors } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { flatten } from 'flat';
import { getParentRepeaterKeyAndItemIndex } from '../../../../utils/utils/formly.utils';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';

export function genericUtilValidator(
  control: AbstractControl,
  field: FormlyFieldConfig,
  options: Record<string, unknown> = {}
): ValidationErrors | null {
  try {
    const { comparator, validationName, operator } = options as {
      comparator: string;
      validationName: string;
      operator: string;
    };
    let { prop1, prop2 } = options;
    prop1 = prop1 === 'undefined' ? undefined : prop1;
    prop2 = prop2 === 'undefined' ? undefined : prop2;

    if (
      !(
        checkIfFormStateDataIsFetched(prop1, field) &&
        checkIfFormStateDataIsFetched(prop2, field)
      )
    ) {
      return null;
    }

    if (!(comparator && validationName)) {
      return null;
    }
    const prop1Value =
      prop1 === '$value'
        ? control.value
        : getGenericUtilValidatorPropValue(prop1, field, operator);
    const prop2Value =
      prop2 === '$value'
        ? control.value
        : getGenericUtilValidatorPropValue(prop2, field, operator);

    const validation = genericUtilValidatorCompareFunctions(
      prop1Value,
      prop2Value,
      comparator
    );

    if (validation) {
      return null;
    }

    const validationErrorObj: ValidationErrors = {};
    validationErrorObj[validationName] = true;
    return validationErrorObj;
  } catch (e: unknown) {
    return null;
  }
}

function genericUtilValidatorCompareFunctions(
  prop1Value: unknown,
  prop2Value: unknown,
  comparator: string
): boolean {
  let validation = false;

  switch (comparator) {
    case '==':
      validation = calculateLessStrictEqualsValidations(
        prop1Value,
        prop2Value,
        '=='
      );
      break;
    case '===':
      validation = prop1Value === prop2Value;
      break;
    case '!=':
      validation = calculateLessStrictEqualsValidations(
        prop1Value,
        prop2Value,
        '!='
      );
      break;
    case '!==':
      validation = prop1Value !== prop2Value;
      break;
    case '<':
      validation = Number(prop1Value ?? '') < Number(prop2Value ?? '');
      break;
    case '<=':
      validation = Number(prop1Value ?? '') <= Number(prop2Value ?? '');
      break;
    case '>':
      validation = Number(prop1Value ?? '') > Number(prop2Value ?? '');
      break;
    case '>=':
      validation = Number(prop1Value ?? '') >= Number(prop2Value ?? '');
      break;
    case 'includes':
      validation = (<string>prop1Value).includes(<string>prop2Value);
      break;
    case 'notContains':
      validation = !(<string>prop1Value).includes(<string>prop2Value);
      break;
    case 'endsWith':
      validation = (<string>prop1Value).endsWith(<string>prop2Value);
      break;
    case 'startsWith':
      validation = (<string>prop1Value).startsWith(<string>prop2Value);
      break;
    case 'length':
      validation =
        (<string>prop1Value).length === Number.parseInt(<string>prop2Value);
      break;
    case '%': {
      const num1 = Number(prop1Value);
      const num2 = Number(prop2Value);
      validation =
        !isNaN(num1) && !isNaN(num2) && num2 !== 0 && num1 % num2 === 0;
      break;
    }
    default:
      break;
  }

  return validation;
}

function calculateLessStrictEqualsValidations(
  prop1Value: unknown,
  prop2Value: unknown,
  comparator: '==' | '!='
): boolean {
  const prop1String = String(prop1Value).toLowerCase();
  const prop2String = String(prop2Value).toLowerCase();
  try {
    if (typeof prop1Value === 'string' || typeof prop2Value === 'string') {
      return comparator === '=='
        ? prop1String == prop2String
        : prop1String != prop2String;
    }
    throw new Error('Using normal values in comparisons');
  } catch (e) {
    console.warn(e);
    return comparator === '=='
      ? prop1Value == prop2Value
      : prop1Value != prop2Value;
  }
}

function checkIfFormStateDataIsFetched(
  prop: any,
  field: FormlyFieldConfig
): boolean {
  if (!Array.isArray(prop)) {
    return true;
  }

  let formStateItemIndex = -1;

  [
    EIremboFormlyFormStateKeys.FETCHED_DATA,
    EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA,
  ].some((fetchKey: string) => {
    if (prop.includes(fetchKey)) {
      formStateItemIndex = prop.indexOf(fetchKey);
    }
  });

  if (formStateItemIndex > -1 && prop.length > formStateItemIndex + 1) {
    const value = getGenericUtilValidatorPropValue(
      prop.slice(0, formStateItemIndex + 2),
      field
    );
    return value === undefined ? false : true;
  }

  return true;
}

function getGenericUtilValidatorPropValue(
  prop: unknown,
  field: FormlyFieldConfig,
  operator?: string
): string | undefined | number | Record<string, unknown> {
  if (prop === undefined) {
    return undefined;
  }
  if (typeof prop === 'string' || typeof prop === 'number') {
    return getGenericUtilValidatorPropValueByOperator(prop, operator);
  }

  if (Array.isArray(prop)) {
    let propValue: any = field;

    prop.some((path: string) => {
      let pathKey = undefined;
      if (path === '$i') {
        pathKey = getParentRepeaterKeyAndItemIndex(field)?.itemIndex;
      } else {
        pathKey = path;
      }
      if (
        pathKey !== undefined &&
        (<Record<string, unknown>>propValue)[pathKey]
      ) {
        propValue = (<Record<string, unknown>>propValue)[pathKey];
        return false;
      }
      propValue = undefined;
      return true;
    });

    return getGenericUtilValidatorPropValueByOperator(propValue, operator);
  }
  return undefined;
}

function getGenericUtilValidatorPropValueByOperator(
  value: unknown,
  operation?: string
): any {
  try {
    switch (operation) {
      case 'parseInt':
        return Number.parseInt(<string>value, 10);
      case 'toLowerCase':
        return (<string>value).toLowerCase();
      case 'toUpperCase':
        return (<string>value).toUpperCase();
      default:
        return value;
    }
  } catch (e: any) {
    return value;
  }
}

export function uniqueRepeaterValuePropertyValidator(
  control: AbstractControl,
  field: FormlyFieldConfig,
  options: Record<string, (string | string[])[]> = {}
): ValidationErrors | null {
  const propKeys: (string | string[])[] | undefined = options['propKeys'];
  try {
    if (
      !propKeys ||
      !Array.isArray(propKeys) ||
      propKeys.length < 1 ||
      !control.value ||
      !Array.isArray(control.value) ||
      control.value.length < 1
    ) {
      return null;
    }

    let validation = false;

    propKeys.some((option: string | string[]) => {
      // Check if it's a single field or a combination of fields
      const validateProp: boolean = Array.isArray(option)
        ? uniqueRepeaterValueCombinationHelper(control, option)
        : uniqueRepeaterValuePropHelper(control, option);

      if (validateProp) {
        validation = true;
        return true;
      }
      return false;
    });

    if (validation) {
      return { uniqueRepeaterValues: true };
    }
    return null;
  } catch (e: unknown) {
    return null;
  }
}

function uniqueRepeaterValueCombinationHelper(
  control: AbstractControl,
  properties: string[]
): boolean {
  try {
    const repeaterData: any[] = control.value;
    let validation = false;
    const uniqueCombinations: Set<string> = new Set();

    repeaterData.some(
      (itemValue: Record<string, unknown> | null | undefined) => {
        const flattenedValue: Record<string, unknown> = flatten(itemValue);

        // Check if all properties in the combination exist
        const allPropertiesExist = properties.every(
          prop =>
            flattenedValue?.[prop] !== undefined &&
            flattenedValue?.[prop] !== null
        );

        if (!allPropertiesExist) {
          return false;
        }

        // Create a combination key from all property values
        const combinationKey = properties
          .map(prop => String(flattenedValue[prop]))
          .join('|__|'); // Using a delimiter that's unlikely to appear in actual values

        if (uniqueCombinations.has(combinationKey)) {
          validation = true;
          return true;
        }

        uniqueCombinations.add(combinationKey);
        return false;
      }
    );

    return validation;
  } catch (error: any) {
    return false;
  }
}

function uniqueRepeaterValuePropHelper(
  control: AbstractControl,
  property: string
): boolean {
  try {
    const repeaterData: any[] = control.value;
    let validation = false;
    const uniqueValueList: string[] = [];
    repeaterData.some(
      (itemValue: Record<string, unknown> | null | undefined) => {
        const flattenedValue: Record<string, unknown> = flatten(itemValue);
        if (!flattenedValue?.[property]) {
          return false;
        }
        if (uniqueValueList.includes(String(flattenedValue[property]))) {
          validation = true;
          return true;
        }
        uniqueValueList.push(String(flattenedValue[property]));
        return false;
      }
    );
    return validation;
  } catch (error: any) {
    return false;
  }
}
